import React, { useState, useEffect, useContext, useMemo } from 'react';
import '../styles/Admin.css';
import ToastContext from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import * as challengesApi from '../utils/challengesApi';
import ChallengeModal from './ChallengeModal';
import { FiPlus, FiSearch, FiEdit2, FiTrash2, FiCalendar, FiTarget, FiTrophy, FiInbox } from 'react-icons/fi';
import { isDemoAdmin, DEMO_ADMIN_MESSAGE } from '../utils/adminDemoMode';

// Sport colors mapping
const sportColors = {
  Football: 'football',
  Tennis: 'tennis',
  Basketball: 'basketball',
  Natation: 'swimming',
  Swimming: 'swimming',
  Cyclisme: 'cycling',
  Cycling: 'cycling',
  Running: 'running',
  Course: 'running',
};

// Get unique sports from challenges
const getUniqueSports = (challenges) => {
  const sports = new Set(challenges.map(ch => ch.sportType).filter(Boolean));
  return Array.from(sports);
};

export default function AdminChallenges() {
  const { currentUser } = useAuth();
  const toast = useContext(ToastContext);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [modal, setModal] = useState(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [sportFilter, setSportFilter] = useState('all');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchChallenges = async () => {
    setLoading(true);
    try {
      console.log('🔄 Fetching admin challenges...');
      const res = await challengesApi.getAllChallengesAdmin();
      console.log('✅ Admin challenges response:', res);
      setChallenges(res.challenges || res);
    } catch (err) {
      console.error('❌ Fetch admin challenges error:', err);
      console.error('   Error message:', err.message);
      console.error('   Error status:', err.status);
      console.error('   Error body:', err.body);
      if (toast?.error) toast.error(`Erreur : ${err.message || 'Failed to fetch'}`);
      setChallenges([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && currentUser.isAdmin) {
      console.log('✅ User is admin, fetching challenges...');
      fetchChallenges();
    }
  }, [currentUser]);

  // Filter challenges based on search and sport
  const filteredChallenges = useMemo(() => {
    let filtered = challenges;
    
    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(ch => 
        ch.title?.toLowerCase().includes(query) ||
        ch.sportType?.toLowerCase().includes(query) ||
        ch.difficulty?.toLowerCase().includes(query)
      );
    }
    
    // Filter by sport
    if (sportFilter !== 'all') {
      filtered = filtered.filter(ch => ch.sportType === sportFilter);
    }
    
    return filtered;
  }, [challenges, searchQuery, sportFilter]);

  // Paginate challenges
  const paginatedChallenges = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredChallenges.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredChallenges, currentPage]);

  const totalPages = Math.ceil(filteredChallenges.length / itemsPerPage);

  const openCreate = () => {
    if (isDemoAdmin(currentUser)) return toast?.info(DEMO_ADMIN_MESSAGE);
    setModal({ type: 'create' });
  };
  const openEdit = (challenge) => {
    if (isDemoAdmin(currentUser)) return toast?.info(DEMO_ADMIN_MESSAGE);
    setModal({ type: 'edit', challenge });
  };
  
  const confirmDelete = async () => {
    if (isDemoAdmin(currentUser)) {
      toast?.info(DEMO_ADMIN_MESSAGE);
      setModal(null);
      return;
    }
    try {
      await challengesApi.deleteChallenge(modal.challenge._id);
      if (toast?.success) toast.success('Défi supprimé');
      setModal(null);
      fetchChallenges();
    } catch (err) {
      console.error(err);
      if (toast?.error) toast.error('Erreur suppression');
    }
  };

  const handleSaved = (saved) => {
    setModal(null);
    fetchChallenges();
    if (toast?.success) toast.success('Défi enregistré');
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, sportFilter]);

  if (!currentUser || !currentUser.isAdmin) {
  return (
    <div className="admin-challenges-page" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)', minHeight: '100vh' }}>
        <div className="admin-challenges-container">
          <div style={{ padding: '2rem', textAlign: 'center' }}>
            <h2 style={{ color: '#6b7280' }}>Accès refusé</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-challenges-page" style={{ background: 'linear-gradient(135deg, #e0f2fe 0%, #f0f9ff 100%)' }}>
      <div className="admin-challenges-container">
        {/* Header */}
        <div className="admin-header">
          <h1 className="admin-title">Gestion des défis</h1>
          <button className="admin-create-btn" onClick={openCreate}>
            <FiPlus size={18} />
            Nouveau Défi
          </button>
        </div>

        {/* Toolbar with Search and Filter */}
        <div className="admin-toolbar">
          <div className="admin-search-box">
            <FiSearch className="admin-search-icon" />
            <input
              type="text"
              className="admin-search-input"
              placeholder="Rechercher un défi..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            className="admin-filter-select"
            value={sportFilter}
            onChange={(e) => setSportFilter(e.target.value)}
          >
            <option value="all">Tous les sports</option>
            {getUniqueSports(challenges).map(sport => (
              <option key={sport} value={sport}>{sport}</option>
            ))}
          </select>
        </div>

        {/* Table Card */}
        <div className="admin-table-card">
          {loading ? (
            <div className="admin-loading">
              <div className="admin-loading-spinner"></div>
              <p className="admin-loading-text">Chargement...</p>
            </div>
          ) : filteredChallenges.length === 0 ? (
            <div className="admin-empty-state">
              <FiInbox className="admin-empty-icon" />
              <h3 className="admin-empty-title">Aucun défi créé</h3>
              <p className="admin-empty-text">Commencez par créer votre premier défi</p>
            </div>
          ) : (
            <>
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Titre</th>
                      <th>Difficulté</th>
                      <th>Sport</th>
                      <th>Dates</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedChallenges.map(ch => (
                      <tr key={ch._id}>
                        <td data-label="Titre">{ch.title}</td>
                        <td data-label="Difficulté">
                          <span className={`difficulty-badge ${ch.difficulty?.toLowerCase()}`}>
                            {ch.difficulty}
                          </span>
                        </td>
                        <td data-label="Sport">
                          <span className={`sport-tag ${sportColors[ch.sportType] || 'default'}`}>
                            {ch.sportType}
                          </span>
                        </td>
                        <td data-label="Dates">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <FiCalendar size={14} style={{ color: '#6b7280' }} />
                            {new Date(ch.startDate).toLocaleDateString('fr-FR')} – {new Date(ch.endDate).toLocaleDateString('fr-FR')}
                          </div>
                        </td>
                        <td data-label="Actions">
                          <div className="action-buttons">
                            <button 
                              className="action-btn edit" 
                              onClick={() => openEdit(ch)}
                              title="Modifier"
                            >
                              <FiEdit2 size={16} />
                            </button>
                            <button 
                              className="action-btn delete" 
                              onClick={() => setModal({ type: 'delete', challenge: ch })}
                              title="Supprimer"
                            >
                              <FiTrash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="admin-pagination">
                  <span className="admin-pagination-info">
                    Affichage de {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredChallenges.length)} sur {filteredChallenges.length} défis
                  </span>
                  <div className="admin-pagination-buttons">
                    <button
                      className="admin-pagination-btn"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      Précédent
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        className={`admin-pagination-btn ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="admin-pagination-btn"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Create / Edit Modal */}
        <ChallengeModal
          isOpen={modal && (modal.type === 'create' || modal.type === 'edit')}
          initialData={modal?.challenge || {}}
          onClose={() => setModal(null)}
          onSaved={handleSaved}
          onPreviewChange={(p) => setPreviewData(p)}
        />

        {/* Delete Confirmation Modal */}
        {modal && modal.type === 'delete' && (
          <div className="modal-overlay">
            <div className="modal-content">
              <p>Supprimer le défi « {modal.challenge.title} » ?</p>
              <div className="modal-actions">
                <button className="modal-btn cancel" onClick={() => setModal(null)}>
                  Annuler
                </button>
                <button className="modal-btn confirm" onClick={confirmDelete}>
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

